<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { uniqueId } from 'lodash-es';
import { useAbort } from '@/modules/common/composables/use-abort.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import { COMMENT_MAX_LENGTH } from '@/modules/common/consts/max-length.ts';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { ValueUtil } from '@/modules/common/utils/value.util.ts';
import Validator from '@/modules/common/utils/validator.ts';
import AddressApi, { type AddressFormData } from '@/modules/counterparty/api/address-api.ts';
import type { CounterpartyAddressItem } from '@/modules/counterparty/counterparty-master/types/address-form.ts';
import { useFormTooltips } from '@/modules/ui/composables/use-form-tooltips.ts';
import AddButton from '@/modules/core/components/buttons/AddButton.vue';
import CloseButton from '@/modules/ui/components/buttons/CloseButton.vue';
import SaveButton from '@/modules/ui/components/buttons/SaveButton.vue';
import ModalForm from '@/modules/ui/components/common/ModalForm.vue';
import UiCombobox from '@/modules/ui/components/ui/UiCombobox.vue';
import UiField from '@/modules/ui/components/ui/UiField.vue';
import UiTextarea from '@/modules/ui/components/ui/UiTextarea.vue';

const PH = {
  type: 'counterparty.master.steps.address.form.selectType',
  address: 'counterparty.master.steps.address.form.addressPlaceholder',
  comment: 'ui.common.placeholder.enterNote',
} as const;

type FormModel = {
  type: Option | null;
  address: string;
  comment: string;
};

const EMPTY_FORM: FormModel = { type: null, address: '', comment: '' };

const isOpen = defineModel<boolean>({ required: true });
const props = defineProps<{
  editItem?: CounterpartyAddressItem | null;
  extraTypes?: Option[];
}>();
const emit = defineEmits<{ submit: [value: AddressFormData] }>();

const { signal, abort } = useAbort();
const { tooltipsDisabled, onInput } = useFormTooltips();

const showErrors = ref(false);
const formData = ref<FormModel>({ ...EMPTY_FORM });
const apiTypes = ref<Option[]>([]);
const sessionTypes = ref<Option[]>([]);

const isEdit = computed(() => !!props.editItem);
const title = computed(() =>
  isEdit.value ? 'counterparty.master.steps.address.form.editTitle' : 'counterparty.master.steps.address.form.title'
);

const typeOptions = computed(() => {
  const merged = new Map<string, Option>();

  for (const type of [...apiTypes.value, ...(props.extraTypes ?? []), ...sessionTypes.value]) {
    if (type?.label) {
      merged.set(type.label, { code: type.code || type.label, label: type.label });
    }
  }

  return [...merged.values()];
});

const errors = computed(() => ({
  type: Validator.required(formData.value.type),
  address: Validator.required(formData.value.address.trim()),
}));

const fieldIds = {
  type: uniqueId(IdPrefix.Control),
  address: uniqueId(IdPrefix.Control),
  comment: uniqueId(IdPrefix.Control),
};

function rememberType(type: Option) {
  if (
    apiTypes.value.some((item) => item.label === type.label) ||
    (props.extraTypes ?? []).some((item) => item.label === type.label) ||
    sessionTypes.value.some((item) => item.label === type.label)
  ) {
    return;
  }

  sessionTypes.value = [...sessionTypes.value, { code: type.code || type.label, label: type.label }];
}

function setType(type: Option | null) {
  formData.value = { ...formData.value, type };

  if (type) {
    rememberType(type);
  }
}

const typeModel = computed({
  get: () => formData.value.type,
  set: setType,
});

function resetForm() {
  formData.value = { ...EMPTY_FORM };
  showErrors.value = false;
}

function applyFormFromEditItem() {
  if (!props.editItem) {
    resetForm();
    return;
  }

  const type = { ...props.editItem.type };
  formData.value = {
    type,
    address: props.editItem.address,
    comment: props.editItem.comment,
  };
  showErrors.value = false;
  rememberType(type);
}

watch(isOpen, async (open) => {
  if (!open) {
    abort();
    return;
  }

  applyFormFromEditItem();
  abort();

  const requestSignal = signal.value;

  try {
    const types = await AddressApi.getTypes(requestSignal);

    if (requestSignal.aborted || !isOpen.value) {
      return;
    }

    apiTypes.value = types;
  } catch (error) {
    if (requestSignal.aborted || !isOpen.value) {
      return;
    }

    apiTypes.value = [];
    notification.error('counterparty.notification.error.address.types', error);
  }
});

function submit() {
  const { type, address, comment } = formData.value;
  showErrors.value = !!errors.value.type || !!errors.value.address;

  if (errors.value.type || errors.value.address || !type) {
    return;
  }

  rememberType(type);
  emit('submit', { type, address: address.trim(), comment: comment.trim() });
  isOpen.value = false;
}
</script>

<template>
  <ModalForm
    v-model:open="isOpen"
    width="xs"
    :confirm-on-dirty="false"
    :title
  >
    <template #body>
      <div class="address-form">
        <UiField
          :id="fieldIds.type"
          stretch
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PH.type, formData.type)"
        >
          <UiCombobox
            v-model="typeModel"
            :placeholder="PH.type"
            :options="typeOptions"
            :error="showErrors && !!errors.type"
            with-custom-option
            inline-search
            expanded
            :search-max-length="50"
          />
        </UiField>

        <UiField
          :id="fieldIds.address"
          stretch
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PH.address, formData.address)"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiTextarea
            :id="fieldIds.address"
            v-model="formData.address"
            :placeholder="PH.address"
            :max-length="COMMENT_MAX_LENGTH"
            :rows="3"
            :error="showErrors && !!errors.address"
            @update:model-value="onInput"
          />
        </UiField>

        <UiField
          :id="fieldIds.comment"
          stretch
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PH.comment, formData.comment)"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiTextarea
            :id="fieldIds.comment"
            v-model="formData.comment"
            :placeholder="PH.comment"
            :max-length="COMMENT_MAX_LENGTH"
            :rows="3"
            no-line-breaks
            @update:model-value="onInput"
          />
        </UiField>
      </div>
    </template>

    <template #footer>
      <CloseButton @click="isOpen = false" />
      <SaveButton
        v-if="isEdit"
        @click="submit"
      />
      <AddButton
        v-else
        @click="submit"
      />
    </template>
  </ModalForm>
</template>

<style scoped lang="scss">
.address-form {
  display: flex;
  padding: 8px 0;
  flex-direction: column;
  gap: 12px;
}
</style>
